/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionClientsUpdate } from "../funcs/organizationRemoteSessionClientsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateOrganizationRemoteSessionClientRequest,
  UpdateOrganizationRemoteSessionClientSecurity,
} from "../models/operations/updateorganizationremotesessionclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateOrganizationRemoteSessionClientMutationVariables = {
  request: UpdateOrganizationRemoteSessionClientRequest;
  security?: UpdateOrganizationRemoteSessionClientSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateOrganizationRemoteSessionClientMutationData =
  RemoteSessionClient;

export type UpdateOrganizationRemoteSessionClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateClient organizationRemoteSessionClients
 *
 * @remarks
 * Update a remote_session_client's non-secret fields in the caller's organization. Requires org:admin.
 */
export function useUpdateOrganizationRemoteSessionClientMutation(
  options?: MutationHookOptions<
    UpdateOrganizationRemoteSessionClientMutationData,
    UpdateOrganizationRemoteSessionClientMutationError,
    UpdateOrganizationRemoteSessionClientMutationVariables
  >,
): UseMutationResult<
  UpdateOrganizationRemoteSessionClientMutationData,
  UpdateOrganizationRemoteSessionClientMutationError,
  UpdateOrganizationRemoteSessionClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateOrganizationRemoteSessionClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateOrganizationRemoteSessionClient(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionClients", "update"];
}

export function buildUpdateOrganizationRemoteSessionClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateOrganizationRemoteSessionClientMutationVariables,
  ) => Promise<UpdateOrganizationRemoteSessionClientMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateOrganizationRemoteSessionClient(),
    mutationFn: function updateOrganizationRemoteSessionClientMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateOrganizationRemoteSessionClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionClientsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
